import { type Language } from '@ev-tech/search-modules-api';

import { hasPositiveRangeValue } from './utils';
import { type ListingRoomDescriptor, type ListingSummary } from './types';
import { ROOMS_PRIMARY_COUNTRIES } from './constants';
import { formatNumber } from '../numberFormatters';

type RoomFieldName = ListingRoomDescriptor['fieldName'];
type ListingRoomValues = {
  labelCount: number;
  value: NonNullable<ListingRoomDescriptor['value']>;
};

const getRoomRange = (listing: ListingSummary, fieldName: RoomFieldName) => (fieldName === 'rooms' ? listing.rooms : listing.bedrooms);

const getListingRoomsFieldName = (listing: ListingSummary): RoomFieldName | undefined => {
  const isDevelopmentProject = 'isDevelopmentProject' in listing && listing.isDevelopmentProject;
  const fieldNames: RoomFieldName[] =
    isDevelopmentProject || ('countryAlpha2' in listing && listing.countryAlpha2 && ROOMS_PRIMARY_COUNTRIES.has(listing.countryAlpha2))
      ? ['rooms', 'bedrooms']
      : ['bedrooms', 'rooms'];
  const hasRoomValue = (fieldName: RoomFieldName) =>
    isDevelopmentProject ? (getRoomRange(listing, fieldName)?.min ?? 0) > 0 : hasPositiveRangeValue(getRoomRange(listing, fieldName));

  return fieldNames.find(hasRoomValue);
};

const getListingRoomValues = (
  listing: ListingSummary,
  options: { fieldName: RoomFieldName; language: Language; returnRangeValue: boolean },
): ListingRoomValues | undefined => {
  const range = getRoomRange(listing, options.fieldName);
  // Prefer the minimum value when available, otherwise use the maximum value
  const numericValue = range?.min && range.min > 0 ? range.min : range?.max && range.max > 0 ? range.max : undefined;
  if (!numericValue) return;

  const values =
    options.returnRangeValue && range?.min && range.min > 0 && range.max && range.max > 0 && range.min !== range.max
      ? [range.min, range.max]
      : [numericValue];

  return {
    labelCount: values.at(-1) ?? numericValue,
    value: values.map((value) => formatNumber({ number: value, language: options.language }, { maximumFractionDigits: 2 })).join(' - '),
  };
};

/**
 * Returns the available rooms or bedrooms descriptor, prioritised by listing type and country code.
 * Falls back to the other field when the preferred field has no positive value.
 *
 * When resolving the returned label key, pass `labelCount` as `count` so the
 * translation resolver selects the correct plural label without using the formatted display value.
 * Use `resolveListingDataValue` to apply the translated "from" template for development projects.
 *
 * @param listing - The listing whose rooms or bedrooms should be selected.
 * @param options - Options used to format the selected room value.
 * @param options.language - The language used for number formatting.
 * @param options.returnRangeValue - When `true`, formats distinct positive minimum and maximum values as a range.
 * Defaults to `false`, returning the minimum value when available, otherwise the maximum value.
 * @returns The selected room descriptor with a ready-to-render formatted value. Returns `undefined` when the listing
 * has no positive rooms or bedrooms value.
 */
export function getListingRooms(
  listing: ListingSummary,
  { language, returnRangeValue = false }: { language: Language; returnRangeValue?: boolean },
): ListingRoomDescriptor | undefined {
  const fieldName = getListingRoomsFieldName(listing);
  if (!fieldName) return;

  const roomValues = getListingRoomValues(listing, { fieldName, language, returnRangeValue });
  if (!roomValues) return;

  const isDevelopmentProject = 'isDevelopmentProject' in listing && listing.isDevelopmentProject;

  return {
    fieldName,
    labelKey: `search.listingData.label.${fieldName}`,
    ...(isDevelopmentProject ? { valueKey: 'search.listingData.development.label.rangePrefix' } : {}),
    ...roomValues,
  };
}
